import { createSelector } from '@reduxjs/toolkit';
import { RootSelector, getStore } from '../../reducers';
import { MagicPrimaryAsset, MagicRef, MagicSession, MagicStudioShape } from './model';

export const getMagicStudio: RootSelector<MagicStudioShape> = createSelector(
  getStore,
  (state) => state.magicStudio,
);

export const getVisible: RootSelector<boolean> = createSelector(
  getMagicStudio,
  (magicStudio) => magicStudio.visible,
);

// Staged references shown in the floating tray.
export const getBasket: RootSelector<MagicRef[]> = createSelector(
  getMagicStudio,
  (magicStudio) => magicStudio.basket,
);

// Membership lookup for the per-asset toggle (only asset refs have an id; a
// captured frame is never "toggled"). A record keyed by asset id keeps the
// per-button subscription cheap (recomputed only when the basket changes).
export const getBasketMap: RootSelector<Record<number, true>> = createSelector(
  getBasket,
  (basket) => {
    const map: Record<number, true> = {};
    basket.forEach((ref) => {
      if (ref.kind === 'asset') map[ref.asset.id] = true;
    });
    return map;
  },
);

// The work stashed by the last close — prompt, settings and result. Read by the
// modal to resume, and by the tray to show what is waiting.
export const getSession: RootSelector<MagicSession | null> = createSelector(
  getMagicStudio,
  (magicStudio) => magicStudio.session,
);

// References the open session was launched with; the first is the primary anchor.
export const getSessionRefs: RootSelector<MagicRef[]> = createSelector(
  getMagicStudio,
  (magicStudio) => magicStudio.sessionRefs,
);

// The session's primary asset (first asset-kind ref), used for naming and the
// empty-canvas preview. Null when the session leads with a captured frame or is
// opened empty.
export const getPrimaryAsset: RootSelector<MagicPrimaryAsset | null> = createSelector(
  getSessionRefs,
  (sessionRefs) => {
    const first = sessionRefs.find((ref) => ref.kind === 'asset');
    return first && first.kind === 'asset' ? first.asset : null;
  },
);
